// src/pages/Admin/Adherents/AdherentManagers.jsx
import React, { useEffect, useState } from "react";
import { getUserIdByAdherentId } from "../../../services/adherentServices";
import { getRoles } from "../../../services/userServices";
import { FaEnvelope } from "react-icons/fa";
import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";

export default function AdherentManagers({ adherentId }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    (async () => {
      if (!adherentId) return;
      try {
        const [userData, rolesData] = await Promise.all([
          getUserIdByAdherentId(adherentId),
          getRoles(),
        ]);
        setUsers(Array.isArray(userData) ? userData : []);
        setRoles(Array.isArray(rolesData) ? rolesData : []);
      } catch (err) {
        console.error("Erreur lors de la récupération des utilisateurs:", err);
      }
    })();
  }, [adherentId]);

  const getRoleName = (roleId) =>
    roles.find((r) => r.id === roleId)?.nom || "Inconnu";

  // Badges role (même palette que tes autres pages)
  const roleBadge = (roleId) => {
    switch (roleId) {
      case 1:
        return "bg-red-500 text-white";
      case 3:
        return "bg-green-500 text-white";
      case 2:
      default:
        return "bg-blue-500 text-white";
    }
  };

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête harmonisé */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Gestionnaires (utilisateurs liés)
        </h3>
        <div className="mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Contenu */}
      <div className="px-5 pb-5 sm:px-6 sm:pb-6 mt-4">
        {users.length === 0 ? (
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-700 dark:text-slate-300">
            Aucun gestionnaire trouvé pour cet adhérent.
          </div>
        ) : (
          <ul className="space-y-3">
            {users.map((u) => (
              <li
                key={u.id || u.email}
                className="flex items-start sm:items-center gap-3 sm:gap-4 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-3 bg-slate-50 dark:bg-slate-900/40"
              >
                {/* Avatar */}
                <img
                  src={
                    u.photoURL
                      ? toAbsoluteUserPhotoUrl(u.photoURL)
                      : "/img/user_avatar.jpg"
                  }
                  alt={`${u.prenom || ""} ${u.nom || ""}`}
                  className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700 bg-white"
                />

                {/* Corps (mobile: tout en colonne) */}
                <div className="min-w-0 flex-1">
                  {/* Nom + Rôle (wrap en mobile) */}
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                      {(u.nom || "").toUpperCase()} {u.prenom || ""}
                    </div>
                    <span
                      className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] sm:text-xs font-semibold ${roleBadge(
                        u.role_id
                      )}`}
                      title={`Rôle : ${getRoleName(u.role_id)}`}
                    >
                      {getRoleName(u.role_id)}
                    </span>
                  </div>

                  {/* Email */}
                  <div className="text-xs text-slate-600 dark:text-slate-300 truncate mt-0.5">
                    {u.email || "—"}
                  </div>

                  {/* Actions mobile (le bouton descend) */}
                  {u.email && (
                    <div className="mt-2 sm:hidden">
                      <a
                        href={`mailto:${u.email}`}
                        className="w-full inline-flex justify-center items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800/70 ring-1 ring-slate-200/80 dark:ring-slate-700/70"
                        title="Envoyer un e-mail"
                      >
                        <FaEnvelope className="text-xs" />
                        Mail
                      </a>
                    </div>
                  )}
                </div>

                {/* Action desktop (à droite) */}
                {u.email && (
                  <a
                    href={`mailto:${u.email}`}
                    className="hidden sm:inline-flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/70 ring-1 ring-slate-200/80 dark:ring-slate-700/70"
                    title="Envoyer un e-mail"
                  >
                    <FaEnvelope className="text-xs" />
                    Mail
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
